/**
 * Instrument Detail Drawer
 * 
 * Shared drawer for viewing asset/position details
 * Opens from Assets table row click
 */

import { useState, useEffect } from 'react';
import { formatCurrency } from '@/lib/portfolioCalculations';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { PenLine } from 'lucide-react';
import { InstrumentDetailHeader } from './InstrumentDetailHeader';
import { InstrumentDetailTabs } from './InstrumentDetailTabs';
import { ManualAssetForm } from './forms/ManualAssetForm';
import { useInstrumentDetail, type InstrumentScope } from '@/hooks/useInstrumentDetail';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';

type PositionSummary = {
  name: string;
  symbol?: string;
  quantity?: number;
  market_value?: number;
  currency?: string;
  accountId?: string;
};

interface InstrumentDetailDrawerProps {
  asset: UnifiedAsset | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (asset: UnifiedAsset) => void; // kept for compatibility (not used)
  mode?: 'view' | 'create' | 'position' | 'edit';
  onCreated?: () => void;
  position?: PositionSummary | null;
}

export function InstrumentDetailDrawer({ asset, open, onClose, onEdit, mode = 'view', onCreated, position }: InstrumentDetailDrawerProps) {
  const [scope, setScope] = useState<InstrumentScope>('asset');
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>();
  const [selectedPosition, setSelectedPosition] = useState<any | undefined>();
  // Local drawer mode so we can switch to edit inline
  const [drawerMode, setDrawerMode] = useState<'view' | 'create' | 'position' | 'edit'>(mode);

  const isCreate = drawerMode === 'create';
  const isPosition = drawerMode === 'position';
  const isEdit = drawerMode === 'edit';

  // Keep internal mode in sync with parent when reopening or mode changes
  useEffect(() => {
    if (open) setDrawerMode(mode);
  }, [mode, open]);

  const { data, isLoading, error, refetch } = useInstrumentDetail({
    assetId: isCreate ? null : (asset?.id || null),
    scope,
    accountId: selectedAccountId,
  });

  const handleScopeToggle = () => {
    setScope(prev => prev === 'asset' ? 'position' : 'asset');
  };

  const handlePositionClick = (position: any) => {
    setSelectedPosition(position);
    setSelectedAccountId(position?.account_id);
    setScope('position');
  };

  const handleBackToAccount = () => {
    setSelectedPosition(undefined);
    setSelectedAccountId(undefined);
    setScope('asset');
  };

  // Determine if scope toggle should be shown
  const showScopeToggle = data && data.positions.length > 1;

  // Removed auto-enter position scope to avoid back navigation loop

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setScope('asset');
      setSelectedPosition(undefined);
      setSelectedAccountId(undefined);
    }
  }, [open]);

  // Defensive reset when switching to a different asset while open
  useEffect(() => {
    if (!open) return;
    if (!asset) return;
    const sourceType = (asset as any)?.source_type;
    if (sourceType === 'account' || sourceType === 'manual') {
      setSelectedPosition(undefined);
      setSelectedAccountId(undefined);
      setScope('asset');
    }
  }, [open, asset?.id]);

  // Guard: avoid being stuck in position scope with no selected account
  useEffect(() => {
    if (scope === 'position' && !selectedAccountId) {
      setScope('asset');
    }
  }, [scope, selectedAccountId]);

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto pt-10 pr-12">
        {isCreate ? (
          <>
            <SheetHeader>
              <SheetTitle className="sr-only">Add Asset</SheetTitle>
              <SheetDescription className="sr-only">Create a new asset</SheetDescription>
            </SheetHeader>
            <div className="py-2">
              <ManualAssetForm
                mode="create"
                onSubmitSuccess={() => { onCreated?.(); onClose(); }}
                onCancel={onClose}
                variant="embedded"
              />
            </div>
          </>
        ) : isEdit && asset ? (
          <>
            <SheetHeader>
              <SheetTitle className="sr-only">Edit Asset</SheetTitle>
              <SheetDescription className="sr-only">Edit existing asset</SheetDescription>
            </SheetHeader>
            {/* Persistent header during edit for consistency */}
            <InstrumentDetailHeader
              asset={(data?.asset as any) || (asset as any)}
              scope={scope}
              showScopeToggle={false}
              onScopeToggle={() => {}}
            />
            {/* Edit-mode nav (aligned with view-mode link bar) */}
            <div className="mt-2 flex items-center gap-4">
              <Button variant="link" size="sm" className="px-0" onClick={() => setDrawerMode('view')}>
                × Cancel
              </Button>
            </div>
            <div className="py-2">
              <ManualAssetForm
                mode="edit"
                initialAsset={(data?.asset as any) || (asset as any)}
                onSubmitSuccess={async () => { await refetch(); setDrawerMode('view'); }}
                onDeleted={async () => { await refetch(); onClose(); }}
                variant="embedded"
                suppressToasts={true}
              />
            </div>
          </>
        ) : isPosition ? (
          <>
            <SheetHeader>
              <SheetTitle>Position Details</SheetTitle>
              <SheetDescription>Read-only summary for a connected holding</SheetDescription>
            </SheetHeader>
            <div className="py-3 space-y-3">
              <div>
                <div className="text-sm text-muted-foreground">Security</div>
                <div className="text-base font-medium">{position?.name || 'Security'}</div>
                {position?.symbol && (
                  <div className="text-xs text-muted-foreground mt-0.5">{position.symbol}</div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Quantity</div>
                  <div className="text-sm font-medium">{position?.quantity ?? '-'}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Market Value</div>
                  <div className="text-sm font-medium">
                    {typeof position?.market_value === 'number'
                      ? formatCurrency(position.market_value, position?.currency || 'CAD')
                      : '-'}
                  </div>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Account</div>
                <div className="text-sm font-medium">{position?.accountId || '-'}</div>
              </div>
            </div>
          </>
        ) : (
          <>
            {isLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-muted-foreground">Loading...</div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-destructive">Error loading asset details</div>
              </div>
            )}

            {data && data.asset && (
              <>
                <SheetHeader>
                  <SheetTitle className="sr-only">
                    {data.asset.asset_name} Details
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    View detailed information about {data.asset.asset_name}
                  </SheetDescription>
                </SheetHeader>

                <InstrumentDetailHeader
                  asset={data.asset as any}
                  scope={scope}
                  showScopeToggle={showScopeToggle}
                  onScopeToggle={handleScopeToggle}
                  position={selectedPosition}
                />

                {(scope === 'position' || (data.asset?.source_type === 'manual')) && (
                  <div className="mt-2 flex items-center gap-4">
                    {scope === 'position' && (
                      <Button variant="link" size="sm" className="px-0" onClick={handleBackToAccount}>
                        ← Back to account
                      </Button>
                    )}
                    {data.asset?.source_type === 'manual' && scope !== 'position' && (
                      <Button variant="link" size="sm" className="px-0" onClick={() => setDrawerMode('edit')}>
                        <PenLine className="mr-1.5 h-3.5 w-3.5" />
                        Edit record
                      </Button>
                    )}
                  </div>
                )}

                <InstrumentDetailTabs
                  asset={data.asset as any}
                  data={data}
                  scope={scope}
                  onPositionClick={handlePositionClick}
                />

                {/* Edit action moved to link bar above */}
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
